import {
  Button,
  Collapse,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  Autocomplete,
  MenuItem,
} from "@mui/material";
import { Icon } from "leaflet";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, Marker, Pane, Polygon, Polyline, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { ThemeProvider } from "styled-components";
import { getAlertTypeNames, getMuiTheme } from "../../../../utils/utils";
import palette, { polylineColors } from "../../../../../config/colorPalette";
import { clone, isEmpty, isNil } from "ramda";
import { ErrorText } from "./styled";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { styled } from "@mui/material/styles";
import ChannelInfo from "./components/channelInfo";
import MeasurementInfo from "./components/measurementInfo";
import { coordsToLatLng, offsetPolygonPerSegment, toPolygon } from "./utils";
import ModalAreas from "./components/modalAreas";
import AlertConfigEquipment from "./components/alertConfigEquipment";
import AddIcon from "@mui/icons-material/Add";
import * as turf from "@turf/turf";

const EquipmentInfo = ({
  equipment,
  setEquipment,
  equipmentCollection,
  polylineDisplay,
  centroid,
  assetName,
  measures,
  setMeasures,
  measuresComputed,
  pIndexes,
  measuresAlerts,
  setMeasuresAlerts,
  thresholdsValues,
  setThresholdsValues,
  thresholdsRanges,
  setThresholdsRanges,
  objectMeasures,
  zones,
  setZones,
  infoToggle,
  setInfoToggle,
  alertConfigToggle,
  setAlertConfigToggle,
  channelAsignToggle,
  setChannelAsignToggle,
  channelNameAsignToggle,
  setChannelNameAsignToggle,
  channelDataToggle,
  setChannelDataToggle,
  channelPK,
  setChannelPK,
  equipmentInterMeasurement,
  setEquipmentInterMeasurement,
  measureToggle,
  setMeasureToggle,
  channelToggle,
  setChannelToggle,
  zoneToggle,
  setZoneToggle,
  moreSubsAllowed,
  substructureAsignToggle,
  setSubstructureAsignToggle,
  substructureDataToggle,
  setSubstructureDataToggle,
  configData,
  criticalPolygon,
  setCriticalPolygon,
  protectionPolygon,
  setProtectionPolygon,
  inclusionArea,
  setInclusionAreas,
  inclinometers,
  setInclinometers,
  areasToggle,
  setAreasToggle,
  selectingCenter,
  setSelectingCenter,
  inclusionPoint,
  setInclusionPoint,
  inclusionRange,
  setInclusionRange,
  exclusionPoint,
  setExclusionPoint,
  selectingExclusion,
  setSelectingExclusion,
  exclusionRange,
  setExclusionRange,
  areasDefToggle,
  setAreasDefToggle,
  mode,
  criticalAreas,
  setCriticalAreas,
  protectionAreas,
  setProtectionAreas,
  assetData,
  errors,
  setErrors,
  assetMeasurands,
  assetModules,
  assetAdditionalSensors,
  assetExternalServices,
  equipmentTypes,
  areaDisplayer,
  mixAreas,
  setMixAreas,
  timeOutDisable,
  setTimeOutDisable,
  intl,
}) => {
  const theme = useMemo(() => getMuiTheme(), []);
  const [open, setOpen] = useState([]);
  const thresholdType = useState(getAlertTypeNames(intl));
  const [hoveredPolylineIndex, setHoveredPolylineIndex] = useState(null);
  const initializedRef = useRef(false);
  const [openConfigEquipment, setOpenConfigEquipment] = useState(false);

  const normalizedPolylines = useMemo(() => {
    return (polylineDisplay ?? []).map((polyline) => ({
      ...polyline,
      points: (polyline.points ?? []).map((p) => ({
        lat: p.latitude,
        lng: p.longitude,
        pk: p.pk,
        id_fo_poss: p.id_fo_poss,
        z: p.z,
      })),
    }));
  }, [polylineDisplay]);
  const handleToggle = (index) => {
    let newOpen = clone(open);
    newOpen[index] = !newOpen[index];
    for (let j = 0; j < newOpen.length; j++) {
      if (newOpen[index] && index !== j) {
        newOpen[j] = false;
      }
    }
    setOpen(newOpen);
  };

  const useStyles = {
    colorSample: {
      width: "20px",
      height: "20px",
      display: "inline-block",
      marginRight: "8px",
      border: "1px solid #ccc",
      borderRadius: "50%",
    },
  };
  const BootstrapButton = styled(Button)({
    marginBottom: "5%",
    border: "1px solid",
    lineHeight: 1.5,
    backgroundColor: palette.primary,
    borderColor: palette.secondary,
    "&:hover": {
      backgroundColor: palette.link,
      borderColor: palette.secondary,
      boxShadow: "none",
    },
    "&:active": {
      boxShadow: "none",
      backgroundColor: palette.link,
      borderColor: palette.secondary,
    },
    "&:focus": {
      boxShadow: "0 0 0 0.2rem rgba(134,26,33,.5)",
    },
  });

  const customCentroidIcon = new Icon({
    iconUrl: require("../../../../../images/Uptech-marker-icon.png"),
    iconSize: [50, 50],
    iconAnchor: [25, 50],
  });
  const customInclinometerIcon = new Icon({
    iconUrl: require("../../../../../images/Uptech-marker-icon-inclinometer.png"),
    iconSize: [50, 50],
    iconAnchor: [25, 50],
  });
  const customIcon = new Icon({
    iconUrl: require("../../../../../images/Uptech-equipment-marker-icon.png"),
    iconSize: [50, 50],
    iconAnchor: [25, 50],
  });

  const customShadowIcon = new Icon({
    iconUrl: require("../../../../../images/Uptech-equipment-marker-icon.png"),
    iconSize: [50, 50],
    iconAnchor: [25, 50],
    shadowUrl: require("../../../../../images/yellow-shadow.png"), // Ajusta la ruta de la sombra según tu estructura de archivos
    shadowSize: [150, 100], // Tamaño de la sombra, ajusta según sea necesario
    shadowAnchor: [74, 75], // Anclaje de la sombra, ajusta según sea necesario
  });

  const [mapType] = useState("regular");

  const AddEquipment = () => {
    // eslint-disable-next-line no-unused-vars
    const map = useMapEvents({
      click: (e) => {
        if (
          equipment.length < equipmentCollection.length &&
          equipmentCollection[0] !== "" &&
          !isEmpty(normalizedPolylines)
        ) {
          setInfoToggle((prev) => [...prev, true]);
          setAlertConfigToggle((prev) => [...prev, false]);
          setAreasToggle((prev) => [...prev, false]);
          setMixAreas((prev) => [...prev, false]);
          setChannelAsignToggle((prev) => [...prev, false]);
          setChannelNameAsignToggle((prev) => [...prev, false]);
          setMeasureToggle((prev) => [...prev, false]);
          setTimeOutDisable((prev) => [...prev, false]);
          const nextType = getRemainingEquipmentType(equipment, equipmentCollection);
          setCriticalAreas((prev) => [...prev, nextType === "DAS" ? 0 : null]);
          setProtectionAreas((prev) => [...prev, nextType === "DAS" ? 0 : null]);
          setChannelDataToggle((prev) => [...prev, [false]]);
          setChannelPK((prev) => {
            const pkIndex = normalizedPolylines.findIndex((np) => np.equipment_type === nextType);

            return [
              ...prev,
              [
                Math.round(
                  normalizedPolylines[pkIndex].points[normalizedPolylines[pkIndex].points.length - 1].pk * 100,
                ) / 100,
              ],
            ];
          });
          setEquipmentInterMeasurement((prev) => [...prev, 0]);
          setSubstructureAsignToggle((prev) => [...prev, [false]]);
          let newChannelToggle = clone(channelToggle);
          let newSubToggle = clone(substructureDataToggle);
          let newZoneToggle = clone(zoneToggle);
          for (let indexEquipment = 0; indexEquipment < Object.keys(measures).length; indexEquipment++) {
            if (isNil(newZoneToggle[indexEquipment]) && isNil(newChannelToggle[indexEquipment])) {
              newChannelToggle.push([]);
              newSubToggle.push([]);
              newZoneToggle.push([]);
              for (
                let indexMeasure = 0;
                indexMeasure < measures[`equipment${indexEquipment + 1}`].length;
                indexMeasure++
              ) {
                newChannelToggle[indexEquipment].push([false]);
                newSubToggle[indexEquipment].push([]);
                newSubToggle[indexEquipment][0].push(false);
                //newChannelToggle[indexEquipment][indexMeasure].push(false);
                newZoneToggle[indexEquipment].push([]);
                newZoneToggle[indexEquipment][0].push([]);
                newZoneToggle[indexEquipment][0][0].push(false);
                //newZoneToggle[indexEquipment][indexMeasure].push([]);
                //newZoneToggle[indexEquipment][indexMeasure][0].push(false);
              }
            }
          }
          setChannelToggle(newChannelToggle);
          setSubstructureDataToggle(newSubToggle);
          setZoneToggle(newZoneToggle);
          setEquipment((prevEquipment) => [
            ...prevEquipment,
            {
              equipmentID: ``,
              name: ``,
              latitude: e.latlng.lat,
              longitude: e.latlng.lng,
              type: getRemainingEquipmentType(prevEquipment, equipmentCollection) === "DAS" ? "SINGLE" : null,
              alertsConfigurations: [],
              measurementInfo: {
                interval: getRemainingEquipmentType(prevEquipment, equipmentCollection) === "DAS" ? null : 0,
                timeout: getRemainingEquipmentType(prevEquipment, equipmentCollection) === "DAS" ? null : 0,
                state: false,
              },
              moreInfo: {
                model: "",
                manufacturer: ``,
                ipAddress: "0.0.0.0",
              },
              statusInfo: {
                supportNotification: false,
                details: "OK",
              },
              asset: assetName,
              equipmentType: getRemainingEquipmentType(prevEquipment, equipmentCollection),
              spatialResolution: getRemainingEquipmentType(prevEquipment, equipmentCollection) === "DAS" ? 0 : null,
              criticalArea:
                getRemainingEquipmentType(prevEquipment, equipmentCollection) === "DAS"
                  ? { distance: 0, polygon: null }
                  : null,
              protectionArea:
                getRemainingEquipmentType(prevEquipment, equipmentCollection) === "DAS"
                  ? { distance: 0, polygon: null }
                  : null,
              areas:
                getRemainingEquipmentType(prevEquipment, equipmentCollection) === "DAS"
                  ? {
                      main: areaDisplayer
                        ? [
                            {
                              polyline: 0,
                              critical: {
                                distance: 0,
                                geometry: {
                                  type: "Polygon",
                                  coordinates: null,
                                },
                              },
                              protection: {
                                distance: 0,
                                geometry: {
                                  type: "Polygon",
                                  coordinates: null,
                                },
                              },
                            },
                          ]
                        : null,
                      avoid: areaDisplayer
                        ? [
                            {
                              distance: 0,
                              geometry: {
                                type: "Point",
                                coordinates: null,
                              },
                            },
                          ]
                        : null,
                      extra: areaDisplayer
                        ? [
                            {
                              distance: 0,
                              geometry: {
                                type: "Point",
                                coordinates: null,
                              },
                            },
                          ]
                        : null,
                    }
                  : null,
              channels: [
                {
                  name: "",
                  info: {
                    desc: "",
                    length:
                      Math.round(normalizedPolylines[0].points[normalizedPolylines[0].points.length - 1].pk * 100) /
                      100,
                    samplingInterval: 0,
                    spatialResolution:
                      getRemainingEquipmentType(prevEquipment, equipmentCollection) === "DAS" ? null : 0,
                    acquisitionFrequency:
                      getRemainingEquipmentType(prevEquipment, equipmentCollection) === "DAS" ? 0 : null,
                    gaugeLength: getRemainingEquipmentType(prevEquipment, equipmentCollection) === "DAS" ? 0 : null,
                  },
                  measurement: {
                    measurementTime: getRemainingEquipmentType(prevEquipment, equipmentCollection) === "DAS" ? null : 0,
                    state: false,
                  },
                  substructures: moreSubsAllowed
                    ? [
                        {
                          name: "",
                          type: "inclinometer",
                          polyline: [0, 0],
                          depth: 50,
                          sections: [""],
                          ref: "",
                          zones: [],
                          equipmentBehavior:
                            getRemainingEquipmentType(prevEquipment, equipmentCollection) === "A-DTS" ? "A-DTS" : null,
                        },
                      ]
                    : [
                        {
                          name: "",
                          type: "generic",
                          polyline: 0,
                          depth: null,
                          sections: null,
                          zones: [],
                          ref: "",
                          equipmentBehavior:
                            getRemainingEquipmentType(prevEquipment, equipmentCollection) === "A-DTS" ? "A-DTS" : null,
                        },
                      ],
                },
              ],
            },
          ]);
          setAreasDefToggle((prev) => [...prev, [false, false]]);
          setSelectingCenter((prev) => [...prev, []]);
          setInclusionPoint((prev) => [...prev, []]);
          setInclusionRange((prev) => [...prev, []]);
          setSelectingExclusion((prev) => [...prev, []]);
          setExclusionPoint((prev) => [...prev, []]);
          setExclusionRange((prev) => [...prev, []]);
          const newErrors = { ...errors };
          newErrors["equipment" + equipment.length + "name"] = "not.empty";
          newErrors["equipment" + equipment.length + "manufacturer"] = "not.empty";
          newErrors["equipment" + equipment.length + "model"] = "not.empty";
          newErrors["equipment" + equipment.length + "equipmentID"] = "not.empty";
          setErrors(newErrors);
        }
      },
    });
    return null;
  };

  const getRemainingEquipmentType = (equipment, equipmentCollection) => {
    const usedEquipmentTypes = equipment.map((item) => item?.equipmentType).filter(Boolean);

    const remainingEquipmentType = equipmentCollection.find((type) => !usedEquipmentTypes.includes(type));

    return remainingEquipmentType || equipmentCollection[0];
  };
  useEffect(() => {
    const inclinometerList = [];
    for (let indexEquipment = 0; indexEquipment < equipment.length; indexEquipment++) {
      for (let channelIndex = 0; channelIndex < equipment[indexEquipment].channels.length; channelIndex++) {
        for (
          let subIndex = 0;
          subIndex < equipment[indexEquipment].channels[channelIndex].substructures.length;
          subIndex++
        ) {
          if (equipment[indexEquipment].channels[channelIndex].substructures[subIndex].type === "inclinometer") {
            inclinometerList.push(equipment[indexEquipment].channels[channelIndex].substructures[subIndex].polyline);
          }
        }
      }
    }

    setInclinometers(inclinometerList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipment]);

  const MyComponentSetView = ({ polyline }) => {
    const map = useMap();
    if (!initializedRef.current && !isEmpty(polyline) && !isNil(polyline[0].points) && !isEmpty(polyline[0].points)) {
      map.fitBounds(polyline[0].points);
      initializedRef.current = true;
    }
    return null;
  };

  return (
    <ThemeProvider theme={theme}>
      <div>
        <Grid Grid container direction="row" justifyContent="flex-start" alignItems="center">
          <Grid item style={{ flex: "1", marginRight: "1%" }}>
            {(isEmpty(normalizedPolylines) || equipmentCollection[0] === "") && (
              <h3>{intl.formatMessage({ id: "first.polyline.and.equipment.to.equipment" })}</h3>
            )}
            <section
              style={{
                position: "relative",
                width: "100%",
                borderRadius: "4px",
                border: `1px solid ${palette.primary}`,
                margin: "1.5rem 0",
                padding: "1%",
              }}
            >
              <h1 style={{ marginBottom: "1%" }}>Equipments</h1>
              {!isNil(equipment) &&
                !isEmpty(equipment) &&
                equipment.map((e, indexEquipment) => {
                  return (
                    <div>
                      <Button id={`equipmetnCollapse${indexEquipment}`} onClick={() => handleToggle(indexEquipment)}>
                        {e.equipmentID} - {e.equipmentType}
                      </Button>
                      <DeleteForeverIcon
                        onClick={() => {
                          const newEquipments = clone(equipment);
                          newEquipments.splice(indexEquipment, 1);
                          setEquipment(newEquipments);
                        }}
                        sx={{ color: palette.primary, cursor: "pointer" }}
                      />
                      <Collapse in={open[indexEquipment]}>
                        <div>
                          <h1
                            onClick={() => {
                              let newInfoToggle = clone(infoToggle);
                              if (newInfoToggle[indexEquipment]) {
                                newInfoToggle[indexEquipment] = false;
                              } else {
                                newInfoToggle[indexEquipment] = true;
                              }
                              setInfoToggle(newInfoToggle);
                            }}
                            style={{ marginBottom: "3%" }}
                          >
                            {intl.formatMessage({ id: "equipment.info" })}
                            {!infoToggle[indexEquipment] ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />}
                          </h1>
                          <Collapse in={infoToggle[indexEquipment]}>
                            <TextField
                              id={"equipment" + indexEquipment + "name"}
                              style={{ margin: "0 0.2rem 0.8rem" }}
                              defaultValue={e.name}
                              onBlur={(e) => {
                                const newErrors = { ...errors };
                                if (isEmpty(e.target.value)) {
                                  newErrors[e.target.id] = "not.empty";
                                } else {
                                  delete newErrors[e.target.id];
                                }
                                setErrors(newErrors);

                                const newEquipments = [...equipment];
                                const updatedEquipment = { ...newEquipments[indexEquipment] };
                                updatedEquipment.name = e.target.value;
                                newEquipments[indexEquipment] = updatedEquipment;

                                setEquipment(newEquipments);
                              }}
                              error={errors["equipment" + indexEquipment + "name"] ? true : false}
                              variant="outlined"
                              label={intl.formatMessage({ id: "equipment.name" })}
                              required
                            ></TextField>
                            <TextField
                              id={"equipment" + indexEquipment + "manufacturer"}
                              style={{ margin: "0 0.2rem 0.8rem" }}
                              defaultValue={e.moreInfo.manufacturer}
                              onBlur={(e) => {
                                const newErrors = { ...errors };
                                if (isEmpty(e.target.value)) {
                                  newErrors[e.target.id] = "not.empty";
                                } else {
                                  delete newErrors[e.target.id];
                                }
                                setErrors(newErrors);

                                const newEquipments = [...equipment];
                                const updatedEquipment = { ...newEquipments[indexEquipment] };
                                updatedEquipment.moreInfo.manufacturer = e.target.value;
                                newEquipments[indexEquipment] = updatedEquipment;

                                setEquipment(newEquipments);
                              }}
                              error={errors["equipment" + indexEquipment + "manufacturer"] ? true : false}
                              variant="outlined"
                              label={intl.formatMessage({ id: "equipment.manufacturer" })}
                              required
                            ></TextField>
                            <TextField
                              id={"equipment" + indexEquipment + "model"}
                              style={{ margin: "0 0.2rem 0.8rem" }}
                              defaultValue={e.moreInfo.model}
                              onBlur={(e) => {
                                const newErrors = { ...errors };
                                if (isEmpty(e.target.value)) {
                                  newErrors[e.target.id] = "not.empty";
                                } else {
                                  delete newErrors[e.target.id];
                                }
                                setErrors(newErrors);

                                const newEquipments = [...equipment];
                                const updatedEquipment = { ...newEquipments[indexEquipment] };
                                updatedEquipment.moreInfo.model = e.target.value;
                                newEquipments[indexEquipment] = updatedEquipment;

                                setEquipment(newEquipments);
                              }}
                              error={errors["equipment" + indexEquipment + "model"] ? true : false}
                              variant="outlined"
                              label={intl.formatMessage({ id: "equipment.model" })}
                              required
                            ></TextField>
                            <TextField
                              id={"equipment" + indexEquipment + "equipmentID"}
                              style={{ margin: "0 0.2rem 0.8rem" }}
                              defaultValue={e.equipmentID}
                              disabled={mode === "edit"}
                              onBlur={(e) => {
                                const newErrors = { ...errors };
                                if (isEmpty(e.target.value)) {
                                  newErrors[e.target.id] = "not.empty";
                                } else {
                                  delete newErrors[e.target.id];
                                }
                                setErrors(newErrors);

                                const newEquipments = [...equipment];
                                const updatedEquipment = { ...newEquipments[indexEquipment] };
                                updatedEquipment.equipmentID = e.target.value;
                                newEquipments[indexEquipment] = updatedEquipment;

                                setEquipment(newEquipments);
                              }}
                              error={errors["equipment" + indexEquipment + "equipmentID"] ? true : false}
                              variant="outlined"
                              label={intl.formatMessage({ id: "equipment.equipmentID" })}
                              required
                            ></TextField>
                            <TextField
                              id={"equipment" + indexEquipment + "_IP"}
                              style={{ margin: "0 0.2rem 0.8rem" }}
                              defaultValue={e.moreInfo.ipAddress}
                              onBlur={(e) => {
                                const newErrors = { ...errors };
                                const regex =
                                  /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
                                const isValidInput = regex.test(e.target.value);
                                if (isEmpty(e.target.value)) {
                                  newErrors[e.target.id] = "not.empty";
                                } else if (!isValidInput) {
                                  newErrors[e.target.id] = "must.ip";
                                } else {
                                  delete newErrors[e.target.id];
                                }
                                setErrors(newErrors);

                                const newEquipments = [...equipment];
                                const updatedEquipment = { ...newEquipments[indexEquipment] };
                                updatedEquipment.moreInfo.ipAddress = e.target.value;
                                newEquipments[indexEquipment] = updatedEquipment;

                                setEquipment(newEquipments);
                              }}
                              error={errors["equipment" + indexEquipment + "_IP"] ? true : false}
                              variant="outlined"
                              label={intl.formatMessage({ id: "ip.address" })}
                              required
                            ></TextField>
                            {!isNil(errors["equipment" + indexEquipment + "_IP"]) && (
                              <ErrorText>
                                {intl.formatMessage({ id: errors["equipment" + indexEquipment + "_IP"] })}
                              </ErrorText>
                            )}
                            <TextField
                              id={"equipment" + indexEquipment + "_channels"}
                              style={{ margin: "0 0.2rem 0.8rem", width: "13.1rem" }}
                              value={e.channels.length}
                              inputProps={{ min: 1, ...(e.equipmentType === "DAS" ? { max: 2 } : {}) }}
                              onChange={(event) => {
                                const newErrors = { ...errors };
                                if (isEmpty(event.target.value)) {
                                  newErrors[event.target.id] = "not.empty";
                                } else {
                                  delete newErrors[event.target.id];
                                }

                                const newEquipments = [...equipment];
                                const updatedEquipment = { ...newEquipments[indexEquipment] };
                                setErrors(newErrors);
                                if (parseInt(event.target.value) === 2 && e.equipmentType === "DAS") {
                                  updatedEquipment.type = "DUAL";
                                }

                                let newThresholdValues = clone(thresholdsValues);
                                let newThresholdRanges = clone(thresholdsRanges);
                                let newZones = clone(zones);
                                let newChannelToggle = clone(channelToggle);
                                let newSubToggle = clone(substructureDataToggle);
                                let newZoneToggle = clone(zoneToggle);

                                updatedEquipment.channels = [];
                                let newChannelDataToggle = clone(channelDataToggle);
                                newChannelDataToggle[indexEquipment] = [];
                                newSubToggle[indexEquipment] = [];
                                let newChannelPk = clone(channelPK);
                                for (let newIndex = 0; newIndex < parseInt(event.target.value); newIndex++) {
                                  newChannelDataToggle[indexEquipment].push(false);
                                  newSubToggle[indexEquipment].push([]);
                                  newSubToggle[indexEquipment][newIndex].push(false);
                                  if (isNil(newChannelPk[indexEquipment][newIndex])) {
                                    const pkIndex =
                                      e.equipmentType === "DAS"
                                        ? normalizedPolylines.findLastIndex(
                                            (np) => np.equipment_type === e.equipmentType,
                                          )
                                        : normalizedPolylines.findIndex((np) => np.equipment_type === e.equipmentType);
                                    newChannelPk[indexEquipment].push(
                                      Math.round(
                                        normalizedPolylines[pkIndex].points[
                                          normalizedPolylines[pkIndex].points.length - 1
                                        ].pk * 100,
                                      ) / 100,
                                    );
                                  }
                                  newChannelToggle[indexEquipment].push(false);
                                  if (isNil(newZones[`equipment${indexEquipment + 1}`][`channel${newIndex + 1}`])) {
                                    newZones[`equipment${indexEquipment + 1}`][`channel${newIndex + 1}`] = {
                                      sub1: [{ ini: 0, end: 1, alertsConfigurations: null }],
                                    };
                                    newZoneToggle[indexEquipment].push([]);
                                    newZoneToggle[indexEquipment][newIndex].push([false]);
                                  }
                                  updatedEquipment.channels.push({
                                    name: "",
                                    info: {
                                      desc: "",
                                      length:
                                        Math.round(
                                          normalizedPolylines[0].points[normalizedPolylines[0].points.length - 1].pk *
                                            100,
                                        ) / 100,
                                      samplingInterval: 0,
                                      spatialResolution:
                                        getRemainingEquipmentType(equipment, equipmentCollection) === "DAS" ? null : 0,
                                      acquisitionFrequency:
                                        getRemainingEquipmentType(equipment, equipmentCollection) === "DAS" ? 0 : null,
                                      gaugeLength:
                                        getRemainingEquipmentType(equipment, equipmentCollection) === "DAS" ? 0 : null,
                                    },
                                    measurement: {
                                      measurementTime:
                                        getRemainingEquipmentType(equipment, equipmentCollection) === "DAS" ? null : 0,
                                      state: false,
                                    },
                                    substructures: moreSubsAllowed
                                      ? [
                                          {
                                            name: "",
                                            type: "inclinometer",
                                            polyline: [0, 0],
                                            depth: 50,
                                            sections: [""],
                                            zones: [],
                                            ref: "",
                                            equipmentBehavior:
                                              getRemainingEquipmentType(equipment, equipmentCollection) === "A-DTS"
                                                ? "A-DTS"
                                                : null,
                                          },
                                        ]
                                      : [
                                          {
                                            name: "",
                                            type: "generic",
                                            polyline: 0,
                                            depth: null,
                                            sections: null,
                                            zones: [],
                                            ref: "",
                                            equipmentBehavior:
                                              getRemainingEquipmentType(equipment, equipmentCollection) === "A-DTS"
                                                ? "A-DTS"
                                                : null,
                                          },
                                        ],
                                  });
                                }

                                //updatedEquipment.channels = parseInt(event.target.value);
                                newEquipments[indexEquipment] = updatedEquipment;
                                setSubstructureDataToggle(newSubToggle);
                                setChannelPK(newChannelPk);
                                setThresholdsValues(newThresholdValues);
                                setThresholdsRanges(newThresholdRanges);
                                setZones(newZones);
                                setEquipment(newEquipments);
                                setChannelToggle(newChannelToggle);
                                setZoneToggle(newZoneToggle);
                                setChannelDataToggle(newChannelDataToggle);
                              }}
                              type="number"
                              error={errors["equipment" + indexEquipment + "_channels"] ? true : false}
                              variant="outlined"
                              label={intl.formatMessage({ id: "measure.channels" })}
                              required
                            ></TextField>
                            {!isNil(errors["equipment" + indexEquipment + "_channels"]) && (
                              <ErrorText>
                                {intl.formatMessage({ id: errors["equipment" + indexEquipment + "_channels"] })}
                              </ErrorText>
                            )}
                            {e.equipmentType === "DAS" && (
                              <div style={{ marginBottom: "5%" }}>
                                <TextField
                                  id={`type${indexEquipment}`}
                                  key={`type${indexEquipment}`}
                                  variant="outlined"
                                  style={{ margin: "0 0.2rem 0.8rem", width: "13.1rem" }}
                                  value={e.type}
                                  disabled={mode === "edit" || e.channels.length === 1}
                                  onChange={(e) => {
                                    const newEquipments = [...equipment];
                                    const updatedEquipment = { ...newEquipments[indexEquipment] };
                                    updatedEquipment.type = e.target.value;
                                    newEquipments[indexEquipment] = updatedEquipment;

                                    setEquipment(newEquipments);
                                  }}
                                  select
                                  label={intl.formatMessage({ id: "type" })}
                                  required
                                >
                                  {e.channels.length === 1 && (
                                    <MenuItem key={`type${indexEquipment}_SINGLE`} value={`SINGLE`}>
                                      {`SINGLE`}
                                    </MenuItem>
                                  )}
                                  <MenuItem key={`type${indexEquipment}_DUAL`} value={`DUAL`}>
                                    {`DUAL`}
                                  </MenuItem>
                                  <MenuItem key={`type${indexEquipment}_DOUBLE`} value={`DOUBLE`}>
                                    {`DOUBLE`}
                                  </MenuItem>
                                </TextField>
                                <TextField
                                  id={`channel_spatial_resolution_${indexEquipment}`}
                                  style={{ margin: "0 0.2rem 0.8rem" }}
                                  inputProps={{ min: 0 }}
                                  defaultValue={e.moreInfo.spatialResolution}
                                  onBlur={(e) => {
                                    const newEquipments = [...equipment];
                                    const updatedEquipment = { ...newEquipments[indexEquipment] };
                                    updatedEquipment.moreInfo.spatialResolution = parseFloat(e.target.value);
                                    newEquipments[indexEquipment] = updatedEquipment;
                                    setEquipment(newEquipments);
                                  }}
                                  error={errors[`channel_spatial_resolution_${indexEquipment}`] ? true : false}
                                  variant="outlined"
                                  label={intl.formatMessage({ id: "channel.spatial.resolution" })}
                                  required
                                  type="number"
                                ></TextField>
                                {areaDisplayer && (
                                  <>
                                    {" "}
                                    <TextField
                                      id={`channel_critical_area_${indexEquipment}`}
                                      style={{ margin: "0 0.2rem 0.8rem" }}
                                      inputProps={{ min: 0 }}
                                      defaultValue={criticalAreas[indexEquipment]}
                                      onBlur={(e) => {
                                        let newCriticalAreas = clone(criticalAreas);
                                        newCriticalAreas[indexEquipment] = parseFloat(e.target.value);
                                        setCriticalAreas(newCriticalAreas);
                                      }}
                                      error={errors[`channel_critical_area_${indexEquipment}`] ? true : false}
                                      variant="outlined"
                                      label={intl.formatMessage({ id: "critical.area" })}
                                      required
                                      type="number"
                                    ></TextField>
                                    <TextField
                                      id={`channel_protection_area_${indexEquipment}`}
                                      style={{ margin: "0 0.2rem 0.8rem" }}
                                      inputProps={{ min: 0 }}
                                      defaultValue={protectionAreas[indexEquipment]}
                                      onBlur={(e) => {
                                        let newProtectionAreas = clone(protectionAreas);
                                        newProtectionAreas[indexEquipment] = parseFloat(e.target.value);
                                        setProtectionAreas(newProtectionAreas);
                                      }}
                                      error={errors[`channel_protection_area_${indexEquipment}`] ? true : false}
                                      variant="outlined"
                                      label={intl.formatMessage({ id: "protection.area" })}
                                      required
                                      type="number"
                                    ></TextField>
                                    {normalizedPolylines?.filter((p) => p.equipment_type === "DAS").length > 1 && (
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            id={"checkbox_areas" + indexEquipment}
                                            sx={{
                                              color: palette.primary,
                                              "&.Mui-checked": {
                                                color: palette.primary,
                                              },

                                              marginLeft: "5%",
                                              marginTop: "2%",
                                            }}
                                            defaultChecked={mixAreas[indexEquipment]}
                                            onChange={(e) => {
                                              let newMixAreas = clone(mixAreas);
                                              newMixAreas[indexEquipment] = e.target.checked;
                                              setMixAreas(newMixAreas);
                                            }}
                                          />
                                        }
                                        label={intl.formatMessage({ id: "mix.areas" })}
                                      />
                                    )}
                                    <br />
                                    <Button
                                      variant="contained"
                                      id="area_button"
                                      style={{
                                        backgroundColor: palette.primary,
                                        color: "white",
                                        margin: "0 0.2rem 0.8rem",
                                        height: "10.5%",
                                      }}
                                      onClick={async () => {
                                        const newEquipments = [...equipment];
                                        const updatedEquipment = { ...newEquipments[indexEquipment] };
                                        const criticalArea = criticalAreas[indexEquipment];
                                        const protectionArea = protectionAreas[indexEquipment];
                                        updatedEquipment.areas.main = [];
                                        const critPolies = [];
                                        const protPolies = [];
                                        let auxPoly = null;
                                        let polyCont = 0;
                                        for (
                                          let indexPolyline = 0;
                                          indexPolyline < normalizedPolylines.length;
                                          indexPolyline++
                                        ) {
                                          if (normalizedPolylines[indexPolyline].equipment_type === "DAS") {
                                            const polys = await offsetPolygonPerSegment(
                                              normalizedPolylines[indexPolyline],
                                              criticalArea,
                                              protectionArea,
                                              updatedEquipment,
                                              intl,
                                            );
                                            critPolies.push(polys.criticalCoords);
                                            protPolies.push(polys.protectCoords);
                                            if (isNil(updatedEquipment.areas.main[polyCont]) && polyCont === 0) {
                                              updatedEquipment.areas.main.push({
                                                polyline: 0,
                                                critical: {
                                                  distance: 0,
                                                  geometry: {
                                                    type: "Polygon",
                                                    coordinates: null,
                                                  },
                                                },
                                                protection: {
                                                  distance: 0,
                                                  geometry: {
                                                    type: "Polygon",
                                                    coordinates: null,
                                                  },
                                                },
                                              });
                                            }
                                            if (!mixAreas[indexEquipment]) {
                                              if (isNil(updatedEquipment.areas.main[polyCont]) && polyCont > 0) {
                                                updatedEquipment.areas.main.push({
                                                  polyline: 0,
                                                  critical: {
                                                    distance: 0,
                                                    geometry: {
                                                      type: "Polygon",
                                                      coordinates: null,
                                                    },
                                                  },
                                                  protection: {
                                                    distance: 0,
                                                    geometry: {
                                                      type: "Polygon",
                                                      coordinates: null,
                                                    },
                                                  },
                                                });
                                              }
                                              updatedEquipment.areas.main[polyCont].polyline = polyCont;
                                              updatedEquipment.areas.main[polyCont].critical.geometry.coordinates = [
                                                critPolies[polyCont],
                                              ];
                                              updatedEquipment.areas.main[polyCont].critical.distance = criticalArea;
                                              updatedEquipment.areas.main[polyCont].protection.geometry.coordinates = [
                                                protPolies[polyCont],
                                              ];
                                              updatedEquipment.areas.main[polyCont].protection.distance =
                                                protectionArea;
                                            } else {
                                              if (isNil(auxPoly)) auxPoly = indexPolyline;
                                            }
                                            polyCont++;
                                          }
                                        }
                                        let finalCritPoliygon = critPolies[0];
                                        let finalProtPoliygon = protPolies[0];
                                        if (mixAreas[indexEquipment]) {
                                          for (let indexCrit = 1; indexCrit < critPolies.length; indexCrit++) {
                                            finalCritPoliygon = turf.union(
                                              turf.featureCollection([
                                                toPolygon(finalCritPoliygon),
                                                toPolygon(critPolies[indexCrit]),
                                              ]),
                                            );
                                          }
                                          for (let indexProt = 1; indexProt < protPolies.length; indexProt++) {
                                            finalProtPoliygon = turf.union(
                                              turf.featureCollection([
                                                toPolygon(finalProtPoliygon),
                                                toPolygon(protPolies[indexProt]),
                                              ]),
                                            );
                                          }
                                          updatedEquipment.areas.main[0].polyline = auxPoly;
                                          updatedEquipment.areas.main[0].critical.geometry.coordinates = coordsToLatLng(
                                            finalCritPoliygon.geometry.coordinates,
                                            finalCritPoliygon.geometry.type,
                                          );

                                          updatedEquipment.areas.main[0].critical.distance = criticalArea;
                                          updatedEquipment.areas.main[0].protection.geometry.coordinates =
                                            coordsToLatLng(
                                              finalProtPoliygon.geometry.coordinates,
                                              finalProtPoliygon.geometry.type,
                                            );

                                          updatedEquipment.areas.main[0].protection.distance = protectionArea;
                                        }
                                        setCriticalPolygon(
                                          mixAreas[indexEquipment]
                                            ? coordsToLatLng(
                                                finalCritPoliygon.geometry.coordinates,
                                                finalCritPoliygon.geometry.type,
                                              )
                                            : critPolies,
                                        );
                                        setProtectionPolygon(
                                          mixAreas[indexEquipment]
                                            ? coordsToLatLng(
                                                finalProtPoliygon.geometry.coordinates,
                                                finalProtPoliygon.geometry.type,
                                              )
                                            : protPolies,
                                        );
                                        newEquipments[indexEquipment] = updatedEquipment;
                                        console.log(updatedEquipment);
                                        setEquipment(newEquipments);
                                      }}
                                    >
                                      {intl.formatMessage({ id: "calculate.area" })}
                                    </Button>
                                    <Button
                                      variant="contained"
                                      style={{
                                        backgroundColor: palette.primary,
                                        color: "white",
                                        margin: "0 0.2rem 0.8rem",
                                        height: "10.5%",
                                      }}
                                      onClick={() => {
                                        let newAreaToggle = clone(areasToggle);
                                        newAreaToggle[indexEquipment] = !areasToggle[indexEquipment];
                                        setAreasToggle(newAreaToggle);
                                      }}
                                    >
                                      {intl.formatMessage({ id: "inclusion.exclusion.areas" })}
                                    </Button>
                                    <br />
                                  </>
                                )}
                              </div>
                            )}
                            <br></br>
                            {areasToggle[indexEquipment] && (
                              <ModalAreas
                                key={`${indexEquipment}_modalareas`}
                                BootstrapButton={BootstrapButton}
                                areasToggle={areasToggle}
                                intl={intl}
                                polylineDisplay={normalizedPolylines}
                                customCentroidIcon={customCentroidIcon}
                                customIcon={customIcon}
                                customInclinometerIcon={customInclinometerIcon}
                                customShadowIcon={customShadowIcon}
                                e={e}
                                equipment={equipment}
                                errors={errors}
                                indexEquipment={indexEquipment}
                                setEquipment={setEquipment}
                                setAreasToggle={setAreasToggle}
                                inclusionArea={inclusionArea}
                                setErrors={setErrors}
                                setInclusionAreas={setInclusionAreas}
                                criticalPolygon={criticalPolygon}
                                protectionPolygon={protectionPolygon}
                                useStyles={useStyles}
                                exclusionPoint={exclusionPoint}
                                exclusionRange={exclusionRange}
                                inclusionPoint={inclusionPoint}
                                inclusionRange={inclusionRange}
                                selectingCenter={selectingCenter}
                                selectingExclusion={selectingExclusion}
                                setExclusionPoint={setExclusionPoint}
                                setExclusionRange={setExclusionRange}
                                setInclusionPoint={setInclusionPoint}
                                setInclusionRange={setInclusionRange}
                                setSelectingCenter={setSelectingCenter}
                                setSelectingExclusion={setSelectingExclusion}
                                areasDefToggle={areasDefToggle}
                                setAreasDefToggle={setAreasDefToggle}
                              />
                            )}
                            <ChannelInfo
                              BootstrapButton={BootstrapButton}
                              channelAsignToggle={channelAsignToggle}
                              polylineColors={polylineColors}
                              polylineDisplay={normalizedPolylines}
                              setChannelAsignToggle={setChannelAsignToggle}
                              setChannelNameAsignToggle={setChannelNameAsignToggle}
                              setChannelPK={setChannelPK}
                              setThresholdsRanges={setThresholdsRanges}
                              thresholdsRanges={thresholdsRanges}
                              useStyles={useStyles}
                              channelDataToggle={channelDataToggle}
                              channelNameAsignToggle={channelNameAsignToggle}
                              channelPK={channelPK}
                              e={e}
                              equipment={equipment}
                              equipmentInterMeasurement={equipmentInterMeasurement}
                              mode={mode}
                              errors={errors}
                              indexEquipment={indexEquipment}
                              setChannelDataToggle={setChannelDataToggle}
                              setEquipment={setEquipment}
                              setEquipmentInterMeasurement={setEquipmentInterMeasurement}
                              hoveredPolylineIndex={hoveredPolylineIndex}
                              setHoveredPolylineIndex={setHoveredPolylineIndex}
                              setErrors={setErrors}
                              moreSubsAllowed={moreSubsAllowed}
                              centroid={centroid}
                              customCentroidIcon={customCentroidIcon}
                              customInclinometerIcon={customInclinometerIcon}
                              customIcon={customIcon}
                              customShadowIcon={customShadowIcon}
                              inclinometers={inclinometers}
                              zoneToggle={zoneToggle}
                              setZoneToggle={setZoneToggle}
                              zones={zones}
                              setZones={setZones}
                              open={open}
                              setSubstructureDataToggle={setSubstructureDataToggle}
                              substructureDataToggle={substructureDataToggle}
                              intl={intl}
                            />

                            {e.equipmentType !== "DAS" && (
                              <TextField
                                id={equipment + indexEquipment + "_frequency"}
                                style={{ margin: "0 0.2rem 0.8rem", width: "50%" }}
                                defaultValue={e.measurementInfo.interval}
                                inputProps={{ max: 86400, min: 0 }}
                                onBlur={(e) => {
                                  const newErrors = { ...errors };
                                  if (isEmpty(e.target.value) || isNaN(e.target.value)) {
                                    newErrors[e.target.id] = "not.empty";
                                  } else if (parseFloat(e.target.value) < equipmentInterMeasurement[indexEquipment]) {
                                    newErrors[e.target.id] = "not.higher.than.amount.measurement.time.channels";
                                  } else if (parseFloat(e.target.value) > 86400) {
                                    newErrors[e.target.id] = "not.higher.than.day";
                                  } else {
                                    delete newErrors[e.target.id];
                                  }
                                  setErrors(newErrors);

                                  const newEquipments = [...equipment];
                                  const updatedEquipment = { ...newEquipments[indexEquipment] };
                                  updatedEquipment.measurementInfo.interval = parseFloat(e.target.value);
                                  newEquipments[indexEquipment] = updatedEquipment;

                                  setEquipment(newEquipments);
                                }}
                                type="number"
                                error={errors["equipment" + indexEquipment + "_frequency"] ? true : false}
                                variant="outlined"
                                label={intl.formatMessage({ id: "measure.frequency" })}
                                required
                              ></TextField>
                            )}

                            {!isNil(errors["equipment" + indexEquipment + "_frequency"]) && (
                              <ErrorText>
                                {intl.formatMessage({ id: errors["equipment" + indexEquipment + "_frequency"] })}
                              </ErrorText>
                            )}
                            <FormControlLabel
                              control={
                                <Checkbox
                                  id={"checkbox" + indexEquipment}
                                  sx={{
                                    color: palette.primary,
                                    "&.Mui-checked": {
                                      color: palette.primary,
                                    },
                                  }}
                                  defaultChecked={e.measurementInfo.state}
                                  onChange={(e) => {
                                    const newErrors = { ...errors };
                                    if (isEmpty(e.target.value)) {
                                      newErrors[e.target.id] = "not.empty";
                                    } else {
                                      delete newErrors[e.target.id];
                                    }
                                    setErrors(newErrors);

                                    const newEquipments = [...equipment];
                                    const updatedEquipment = { ...newEquipments[indexEquipment] };
                                    updatedEquipment.measurementInfo.state = e.target.checked;
                                    newEquipments[indexEquipment] = updatedEquipment;

                                    setEquipment(newEquipments);
                                  }}
                                />
                              }
                              label={intl.formatMessage({ id: "measurement.state" })}
                            />
                            <TextField
                              id={equipment + indexEquipment + "_timeout"}
                              style={{ margin: "0 0.2rem 0.8rem", width: "50%" }}
                              value={e.measurementInfo.timeout}
                              inputProps={{ min: 0, max: 86400 }}
                              InputLabelProps={{ shrink: true }}
                              onChange={(e) => {
                                const newErrors = { ...errors };
                                if (isEmpty(e.target.value)) {
                                  newErrors[e.target.id] = "not.empty";
                                } else {
                                  delete newErrors[e.target.id];
                                }
                                setErrors(newErrors);

                                const newEquipments = [...equipment];
                                const updatedEquipment = { ...newEquipments[indexEquipment] };
                                updatedEquipment.measurementInfo.timeout = parseFloat(e.target.value);
                                newEquipments[indexEquipment] = updatedEquipment;

                                setEquipment(newEquipments);
                              }}
                              type="number"
                              error={errors[equipment + indexEquipment + "_timeout"] ? true : false}
                              variant="outlined"
                              label={intl.formatMessage({ id: "timeout" })}
                              required
                              disabled={timeOutDisable[indexEquipment]}
                            ></TextField>
                            {!isNil(errors[equipment + indexEquipment + "_timeout"]) && (
                              <ErrorText>
                                {intl.formatMessage({ id: errors[equipment + indexEquipment + "_timeout"] })}
                              </ErrorText>
                            )}
                            <FormControlLabel
                              control={
                                <Checkbox
                                  id={"checkbox_timeout" + equipment + indexEquipment}
                                  sx={{
                                    color: palette.primary,
                                    "&.Mui-checked": {
                                      color: palette.primary,
                                    },
                                  }}
                                  onChange={(e) => {
                                    let newTimeOutDisable = clone(timeOutDisable);
                                    newTimeOutDisable[indexEquipment] = e.target.checked;
                                    setTimeOutDisable(newTimeOutDisable);
                                    const newEquipments = [...equipment];
                                    const updatedEquipment = { ...newEquipments[indexEquipment] };
                                    updatedEquipment.measurementInfo.timeout = e.target.checked ? null : 100;

                                    const newErrors = { ...errors };
                                    if (e.target.checked) delete newErrors[equipment + indexEquipment + "_timeout"];
                                    setErrors(newErrors);
                                    newEquipments[indexEquipment] = updatedEquipment;

                                    setEquipment(newEquipments);
                                  }}
                                  checked={timeOutDisable[indexEquipment]}
                                />
                              }
                              label={intl.formatMessage({ id: "disable?" })}
                            />
                          </Collapse>
                        </div>
                        <h1
                          onClick={() => {
                            let newAlertConfigToggle = clone(alertConfigToggle);
                            if (newAlertConfigToggle[indexEquipment]) {
                              newAlertConfigToggle[indexEquipment] = false;
                            } else {
                              newAlertConfigToggle[indexEquipment] = true;
                            }
                            setAlertConfigToggle(newAlertConfigToggle);
                          }}
                          style={{ marginBottom: "3%" }}
                        >
                          {intl.formatMessage({ id: "alert.config" })}
                          {!alertConfigToggle[indexEquipment] ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />}
                        </h1>
                        <Collapse in={alertConfigToggle[indexEquipment]}>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "7.5%" }}>
                            <Autocomplete
                              multiple
                              id="measurand_select"
                              variant="outlined"
                              options={
                                configData.alertConfiguration?.filter((option) => option.category === "equipment") || []
                              }
                              value={!isNil(e.alertsConfigurations) ? e.alertsConfigurations : []}
                              sx={{
                                width: "16rem", // aumenta el ancho para que quepan más chips
                                ".MuiChip-root": {
                                  maxWidth: "100%", // permite que los chips no se corten
                                },
                                ".MuiAutocomplete-tag": {
                                  maxWidth: "calc(100% - 16px)", // previene overflow
                                },
                                margin: "0 0.2rem",
                              }}
                              disableCloseOnSelect
                              onChange={(event, values) => {
                                const newEquipments = [...equipment];
                                const updatedEquipment = { ...newEquipments[indexEquipment] };
                                updatedEquipment.alertsConfigurations = values;
                                newEquipments[indexEquipment] = updatedEquipment;
                                setEquipment(newEquipments);
                              }}
                              getOptionLabel={(option) => option.name}
                              isOptionEqualToValue={(option, value) => option.name === value.name}
                              renderOption={(props, option, { selected }) => (
                                <li {...props}>
                                  <Checkbox style={{ marginRight: 8 }} checked={selected} />
                                  {option.name}
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="outlined"
                                  label={intl.formatMessage({ id: "alert.config" })}
                                />
                              )}
                              required
                            />
                            <IconButton
                              style={{ backgroundColor: palette.dividerLight }}
                              onClick={() => {
                                //Abrir el Modal para crear una configuración de alerta
                                setOpenConfigEquipment(true);
                              }}
                            >
                              <AddIcon sx={{ color: palette.primary }} />
                            </IconButton>
                            {openConfigEquipment && (
                              <AlertConfigEquipment
                                open={openConfigEquipment}
                                setOpen={setOpenConfigEquipment}
                                configData={configData}
                                selectedEquipment={e}
                                equipmentTypes={equipmentTypes}
                              />
                            )}
                          </div>
                        </Collapse>

                        <h1
                          onClick={() => {
                            let newMeasureToggle = clone(measureToggle);
                            if (newMeasureToggle[indexEquipment]) {
                              newMeasureToggle[indexEquipment] = false;
                            } else {
                              newMeasureToggle[indexEquipment] = true;
                            }
                            setMeasureToggle(newMeasureToggle);
                          }}
                          style={{ marginBottom: "3%", marginTop: "2.5%" }}
                        >
                          {intl.formatMessage({ id: "measure.information" })}
                          {!measureToggle[indexEquipment] ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />}
                        </h1>

                        <MeasurementInfo
                          e={e}
                          indexEquipment={indexEquipment}
                          //indexMeasure={indexMeasure}
                          //measure={measure}
                          measureToggle={measureToggle}
                          measures={measures}
                          channelToggle={channelToggle}
                          equipment={equipment}
                          errors={errors}
                          setErrors={setErrors}
                          intl={intl}
                          setThresholdsValues={setThresholdsValues}
                          setZoneToggle={setZoneToggle}
                          setZones={setZones}
                          thresholdType={thresholdType}
                          thresholdsRanges={thresholdsRanges}
                          thresholdsValues={thresholdsValues}
                          zoneToggle={zoneToggle}
                          zones={zones}
                          measuresAlerts={measuresAlerts}
                          measuresComputed={measuresComputed}
                          polylineDisplay={normalizedPolylines}
                          setChannelToggle={setChannelToggle}
                          setMeasures={setMeasures}
                          setMeasuresAlerts={setMeasuresAlerts}
                          setThresholdsRanges={setThresholdsRanges}
                          configData={configData}
                          moreSubsAllowed={moreSubsAllowed}
                          setSubstructureDataToggle={setSubstructureDataToggle}
                          substructureDataToggle={substructureDataToggle}
                          key={`equipmentZoneManagement${indexEquipment}`}
                          assetData={assetData}
                          assetAdditionalSensors={assetAdditionalSensors}
                          assetExternalServices={assetExternalServices}
                          assetMeasurands={assetMeasurands}
                          assetModules={assetModules}
                          equipmentTypes={equipmentTypes}
                        />

                        {/*!measuresComputed[`equipment${indexEquipment + 1}`].every((elemento) =>
                          measures[`equipment${indexEquipment + 1}`].includes(elemento),
                        ) &&
                        !isNil(objectMeasures.find((obj) => obj.name === e.equipmentType).measures) &&
                        !isNil(objectMeasures.find((obj) => obj.name === e.equipmentType).computing) &&
                        measures[`equipment${indexEquipment + 1}`].length <
                          objectMeasures.find((obj) => obj.name === e.equipmentType).measures.length +
                            objectMeasures.find((obj) => obj.name === e.equipmentType).computing.length ? (
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <IconButton
                              style={{ backgroundColor: palette.dividerLight }}
                              key="add_magnitude"
                              onClick={() => {
                                //setTuples([...tuples, { type: null, value: null }]);
                                const localMeasures = clone(measures);
                                const localMeasuresAlerts = clone(measuresAlerts);
                                const localThresholdsValues = clone(thresholdsValues);
                                localMeasures[`equipment${indexEquipment + 1}`].push("");
                                localMeasuresAlerts[`equipment${indexEquipment + 1}`].push(0);
                                localThresholdsValues[`equipment${indexEquipment + 1}`].push([
                                  [[{ type: null, value: 0.5 }]],
                                ]);
                                setMeasures(localMeasures);
                                setMeasuresAlerts(localMeasuresAlerts);
                                setThresholdsValues(localThresholdsValues);
                              }}
                            >
                              <AddIcon sx={{ color: palette.primary }} />
                            </IconButton>
                            <div
                              style={{
                                width: "100%",
                                height: "1px",
                                backgroundColor: palette.primary,
                                margin: "8px 0",
                              }}
                            ></div>
                          </div>
                        ) : null*/}
                      </Collapse>
                    </div>
                  );
                })}
            </section>
          </Grid>
          <Grid item style={{ flex: "2" }}>
            <MapContainer
              id="map-equipment-id"
              center={[43, 0]}
              zoom={5}
              style={{ height: "calc(100vh - 320px)", width: "60vw" }}
              rotateControl={false}
              anima
            >
              <TileLayer
                attribution={window.RUNTIME_CONFIG.IS_ONLINE ? "Google Maps" : "OpenStreetMap"}
                url={window.RUNTIME_CONFIG.MAP_TYPES.find((type) => type.value === mapType).tileLayer}
                maxZoom={18}
                subdomains={["mt0", "mt1", "mt2", "mt3"]}
              />
              <Pane name="polygonsPane" style={{ zIndex: 400 }} />
              <Pane name="linesPane" style={{ zIndex: 500 }} />
              {centroid.length > 0 && <Marker key={"Centroid"} position={centroid} icon={customCentroidIcon}></Marker>}
              <MyComponentSetView polyline={normalizedPolylines} />
              {isNil(hoveredPolylineIndex) &&
                normalizedPolylines.length > 0 &&
                // eslint-disable-next-line array-callback-return
                normalizedPolylines.map((polyline, index) => {
                  // eslint-disable-next-line array-callback-return
                  return polyline.points.map((point, indice) => {
                    if (indice === 0 || polyline.must_graph_points.indexOf(indice) === 0) {
                      return null;
                    }

                    if (polyline.must_graph_points.includes(indice)) {
                      const previousIndex = polyline.must_graph_points[polyline.must_graph_points.indexOf(indice) - 1];
                      const center = [
                        [
                          normalizedPolylines[index].points[previousIndex].lat,
                          normalizedPolylines[index].points[previousIndex].lng,
                        ],
                        [point.lat, point.lng],
                      ];
                      let auxColor = "black";
                      for (let indexEquipment = 0; indexEquipment < equipment.length; indexEquipment++) {
                        //if (equipment[indexEquipment].channels.includes(index)) {
                        //  auxColor = "red";
                        //}
                      }
                      const color = normalizedPolylines.length > 10 ? auxColor : polylineColors[index];
                      return (
                        <Polyline key={indice + "_" + index} color={color} positions={center} weight={5}></Polyline>
                      );
                    }
                  });
                })}
              {!isNil(criticalPolygon) &&
                !isEmpty(criticalPolygon) &&
                criticalPolygon.map((poly, polyIndex) => (
                  <Polygon
                    positions={poly}
                    pane="polygonsPane"
                    key={`crit${polyIndex}`}
                    pathOptions={{ color: "red" }}
                  />
                ))}
              {!isNil(protectionPolygon) &&
                !isEmpty(protectionPolygon) &&
                protectionPolygon.map((poly, polyIndex) => (
                  <Polygon
                    positions={poly}
                    pane="polygonsPane"
                    key={`prot${polyIndex}`}
                    pathOptions={{ color: "green" }}
                  />
                ))}
              {!isNil(hoveredPolylineIndex) &&
                normalizedPolylines.length > 0 &&
                // eslint-disable-next-line array-callback-return
                normalizedPolylines.map((polyline, index) => {
                  // eslint-disable-next-line array-callback-return
                  return polyline.points.map((point, indice) => {
                    if (indice === 0 || polyline.must_graph_points.indexOf(indice) === 0) {
                      return null;
                    }

                    if (polyline.must_graph_points.includes(indice)) {
                      const previousIndex = polyline.must_graph_points[polyline.must_graph_points.indexOf(indice) - 1];
                      const center = [
                        [
                          normalizedPolylines[index].points[previousIndex].lat,
                          normalizedPolylines[index].points[previousIndex].lng,
                        ],
                        [point.lat, point.lng],
                      ];
                      let auxColor = "black";
                      for (let indexEquipment = 0; indexEquipment < equipment.length; indexEquipment++) {
                        //if (equipment[indexEquipment].channels.includes(index)) {
                        //  auxColor = "red";
                        //}
                      }
                      if (index === hoveredPolylineIndex) {
                        auxColor = "#0dff00";
                      }
                      const color = auxColor;
                      return (
                        <Polyline key={indice + "_" + index} color={color} positions={center} weight={5}></Polyline>
                      );
                    }
                  });
                })}
              <AddEquipment />
              {equipment.length > 0 &&
                equipment.map((equip, index) => (
                  <Marker
                    key={"equipment" + index}
                    position={[equip.latitude, equip.longitude]}
                    icon={open[index] ? customShadowIcon : customIcon}
                  ></Marker>
                ))}
            </MapContainer>
          </Grid>
        </Grid>
      </div>
    </ThemeProvider>
  );
};
export default EquipmentInfo;
